import { useCallback, useEffect, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * שדה חיפוש מקומי + העברה להורה רק אחרי debounce — מפחית רינדורים כבדים בזמן הקלדה.
 * סריקת ברקוד / commit מיידי: applyImmediate(value).
 */
export function useDebouncedSearchField(
  committedValue: string,
  onCommit: (value: string) => void,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) {
  const [draft, setDraft] = useState(committedValue);

  useEffect(() => {
    setDraft(committedValue);
  }, [committedValue]);

  useEffect(() => {
    if (draft.trim() === committedValue.trim()) return;
    const t = window.setTimeout(() => {
      onCommit(draft);
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [draft, committedValue, debounceMs, onCommit]);

  const applyImmediate = useCallback(
    (value: string) => {
      setDraft(value);
      onCommit(value);
    },
    [onCommit],
  );

  return { draft, setDraft, applyImmediate };
}

/**
 * ערך מדובנס גנרי לשימוש רחב (חיפוש/פילטור/אימות קלט).
 */
export function useDebouncedValue<T>(value: T, debounceMs = DEFAULT_DEBOUNCE_MS): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedValue(value);
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [value, debounceMs]);

  return debouncedValue;
}
